<script setup>
import { inject, watch } from 'vue';
import { isEmpty } from 'lodash';
import { useEventListener } from '@vueuse/core';
import useSelectCifra, { tieneEstructuraAgrupada } from './useSelectCifra';

const props = defineProps({
  opciones: { type: Array, default: () => [] },
  seleccionInicial: { type: Array, default: () => [] },
  multiple: { type: Boolean, default: false },
  campoEtiqueta: { type: String, default: undefined },
  campoId: { type: String, default: undefined },
  marcador: { type: String, default: undefined },
  buscable: { type: Boolean, default: true },
  permitirVacio: { type: Boolean, default: true },
  deshabilitado: { type: Boolean, default: false },
  cargando: { type: Boolean, default: false },
  mostrarLimpiar: { type: Boolean, default: false },
  mostrarEtiquetas: { type: Boolean, default: false },
  nombreInput: { type: String, default: undefined },
  valoresGrupo: { type: String, default: undefined },
  etiquetaGrupo: { type: String, default: undefined },
  seleccionarGrupo: { type: Boolean, default: false },
  etiquetaPersonalizada: { type: Function, default: undefined },
  permiteEtiquetas: { type: Boolean, default: false },
  busquedaInterna: { type: Boolean, default: true },
  limite: { type: Number, default: undefined },
  ocultarSeleccionados: { type: Boolean, default: false },
  autofoco: { type: Boolean, default: false },
});

const emit = defineEmits(['cambio-busqueda', 'nueva-etiqueta']);

const modelInterno = defineModel({
  type: [Object, Array, String, Number, Boolean],
  default: null,
});

if (props.multiple && !Array.isArray(modelInterno.value)) {
  modelInterno.value = [];
}

function aplanarOpcionesSeleccion(opciones) {
  if (
    !props.valoresGrupo
    || !props.etiquetaGrupo
    || !tieneEstructuraAgrupada(opciones, props.valoresGrupo)
  ) {
    return opciones;
  }
  return opciones.reduce((prev, curr) => {
    const valores = curr[props.valoresGrupo];
    if (Array.isArray(valores) && valores.length) {
      return prev.concat(valores);
    }
    return prev;
  }, []);
}

function aplicarSeleccionInicial(nuevasOpciones) {
  if (nuevasOpciones.length === 0) return false;

  const opciones = aplanarOpcionesSeleccion(nuevasOpciones);
  const campoId = props.campoId || 'id';
  const coincidencias = opciones.filter(
    (opcion) => props.seleccionInicial.includes(opcion[campoId]),
  );
  if (coincidencias.length > 0) {
    modelInterno.value = props.multiple ? coincidencias : (coincidencias[0] ?? null);
  }
  return true;
}

if (!isEmpty(props.seleccionInicial) && !aplicarSeleccionInicial(props.opciones)) {
  const detenerWatch = watch(
    () => props.opciones,
    (nuevasOpciones) => {
      if (aplicarSeleccionInicial(nuevasOpciones)) {
        detenerWatch();
      }
    },
  );
}

const $I18n = inject('I18n');

const {
  abierto,
  busqueda,
  contenedorRef,
  triggerRef,
  menuRef,
  inputBusquedaRef,
  opcionesFiltradas,
  valoresVisibles,
  cantidadOculta,
  etiquetaSeleccionada,
  mostrarMarcador,
  marcadorInput,
  obtenerEtiquetaOpcion,
  estaSeleccionada,
  opcionDeshabilitada,
  esResaltada,
  resaltarOpcion,
  quitarResaltado,
  seleccionarOpcion,
  quitarOpcion,
  limpiarTodo,
  toggle,
  actualizarBusqueda,
  onKeydownTrigger,
  onKeydownInput,
  obtenerValorInputOculto,
  valorInterno,
  estaGrupoSeleccionado,
  abrir,
} = useSelectCifra(props, emit, modelInterno);

useEventListener(contenedorRef, 'select-cifra:limpiar', () => {
  limpiarTodo();
});

watch(modelInterno, (nuevoValor) => {
  contenedorRef.value?.dispatchEvent(new CustomEvent('change', {
    bubbles: true,
    detail: { value: nuevoValor },
  }));
});

function textoEtiquetaAccion(opcion) {
  if (opcion.$isLabel) {
    return estaGrupoSeleccionado(opcion)
      ? $I18n.t('pulse_enter_deseleccionar_grupo')
      : $I18n.t('pulse_enter_seleccionar_grupo');
  }
  if (estaSeleccionada(opcion)) {
    return $I18n.t('pulse_enter_deseleccionar');
  }
  return $I18n.t('pulse_enter_seleccionar');
}
</script>

<template>
  <div
    ref="contenedorRef"
    class="select-cifra"
    :class="{
      'select-cifra--activo': abierto,
      'select-cifra--deshabilitado': deshabilitado,
      'select-cifra--multiple': multiple,
      'select-cifra--con-limpiar': mostrarLimpiar,
    }"
  >
    <div
      ref="triggerRef"
      class="select-cifra__trigger form-control"
      role="combobox"
      :aria-expanded="abierto"
      :tabindex="deshabilitado ? -1 : 0"
      @keydown="onKeydownTrigger"
      @mousedown.prevent="toggle"
    >
      <a
        v-if="mostrarLimpiar && valorInterno.length > 0"
        class="select-cifra__limpiar"
        href="#"
        @click.prevent.stop="limpiarTodo"
      >
        <i class="fa fa-times"></i>
      </a>

      <div class="select-cifra__contenido">
        <template v-if="multiple">
          <span
            v-for="(opcion, indice) in valoresVisibles"
            :key="indice"
            class="select-cifra__tag label label-default"
            @mousedown.prevent
          >
            {{ obtenerEtiquetaOpcion(opcion) }}
            <i
              class="select-cifra__tag-icon fa fa-times"
              tabindex="0"
              @mousedown.prevent.stop="quitarOpcion(opcion)"
            ></i>
          </span>
          <strong
            v-if="cantidadOculta > 0"
            class="select-cifra__mas"
          >
            {{ $I18n.t('y_n_mas', { count: cantidadOculta }) }}
          </strong>
        </template>

        <span
          v-if="!multiple && valorInterno.length > 0 && (!buscable || !abierto)"
          class="select-cifra__seleccion"
        >
          {{ etiquetaSeleccionada }}
        </span>

        <input
          v-if="buscable && (multiple || abierto || !valorInterno.length || busqueda)"
          ref="inputBusquedaRef"
          class="select-cifra__input"
          type="text"
          autocomplete="off"
          :placeholder="marcadorInput"
          :value="busqueda"
          :disabled="deshabilitado"
          @input="actualizarBusqueda($event.target.value)"
          @keydown.stop="onKeydownInput"
          @mousedown.stop
          @click.stop="abrir()"
        >

        <span
          v-if="mostrarMarcador"
          class="select-cifra__marcador"
        >
          {{ marcador }}
        </span>
      </div>

      <span v-if="cargando" class="select-cifra__spinner">
        <i class="fa fa-spinner fa-spin"></i>
      </span>

      <span class="select-cifra__flecha">
        <i class="fa fa-chevron-down"></i>
      </span>
    </div>

    <ul
      v-show="abierto"
      ref="menuRef"
      class="select-cifra__menu dropdown-menu"
      role="listbox"
      :aria-multiselectable="multiple"
      @mousedown.prevent
      @mouseleave="quitarResaltado"
    >
      <template v-if="opcionesFiltradas.length > 0">
        <li
          v-for="(opcion, indice) in opcionesFiltradas"
          :key="indice"
          role="presentation"
        >
          <a
            href="#"
            role="option"
            class="select-cifra__opcion"
            :class="{
              'select-cifra__opcion--seleccionada': (
                estaSeleccionada(opcion) && !opcion.$isLabel
              ),
              'select-cifra__opcion--resaltada': esResaltada(opcion),
              'select-cifra__opcion--grupo': opcion.$isLabel,
              'select-cifra__opcion--deshabilitada': opcionDeshabilitada(opcion),
            }"
            :aria-selected="estaSeleccionada(opcion)"
            @mouseenter="resaltarOpcion(opcion)"
            @click.prevent="seleccionarOpcion(opcion)"
          >
            <slot name="opcion" :opcion="opcion">
              <span>{{ obtenerEtiquetaOpcion(opcion) }}</span>
            </slot>
            <span
              v-if="mostrarEtiquetas && !opcion.$isDisabled"
              class="select-cifra__accion pull-right"
            >
              {{ textoEtiquetaAccion(opcion) }}
            </span>
          </a>
        </li>
      </template>
      <li v-else role="presentation">
        <a href="#" class="select-cifra__opcion select-cifra__opcion--vacia">
          <slot name="sinOpciones">
            {{ $I18n.t('No_hay_registros') }}
          </slot>
        </a>
      </li>
    </ul>

    <template v-if="nombreInput !== undefined">
      <template v-if="multiple">
        <input
          v-for="(opcion, indice) in valorInterno"
          :key="indice"
          :name="`${nombreInput}[]`"
          type="hidden"
          :value="obtenerValorInputOculto(opcion)"
        >
      </template>
      <input
        v-else
        type="hidden"
        :name="nombreInput"
        :value="valorInterno.length > 0 ? obtenerValorInputOculto(valorInterno[0]) : ''"
      >
    </template>
  </div>
</template>

<style lang="scss">
@import '@/../assets/stylesheets/cifra/vars/main.scss';

$flecha-ancho: 24px;

.select-cifra {
  position: relative;
  width: 100%;

  &__trigger {
    position: relative;
    display: flex;
    align-items: stretch;
    min-height: $control-height;
    height: auto;
    padding: 0 $flecha-ancho 0 8px;
    margin-bottom: 0;
    cursor: pointer;
    background-color: #fff;
    touch-action: manipulation;

    &:focus {
      border-color: $color-borde-input-focus;
      outline: 0;
    }
  }

  &--activo &__trigger {
    border-color: $color-borde-input-focus;
  }

  &--deshabilitado &__trigger {
    cursor: not-allowed;
    background-color: $grey-background;
    opacity: 0.65;
  }

  &--con-limpiar &__trigger {
    padding-right: 52px;
  }

  &__contenido {
    display: flex;
    flex: 1;
    flex-wrap: wrap;
    align-items: center;
    gap: 2px;
    min-height: 30px;
    max-height: 64px;
    overflow: auto;
    padding: 2px 0;
  }

  &__input {
    flex: 1;
    min-width: 60px;
    border: 0;
    outline: 0;
    background: transparent;
    min-height: 26px;
    line-height: 26px;
    margin: 0;
    padding: 0;
    color: $text-color;
    font-size: inherit;
    box-shadow: none;
  }

  &__marcador,
  &__seleccion {
    line-height: 30px;
    color: $text-color;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__marcador {
    color: #999;
  }

  &__tag {
    display: inline-flex;
    align-items: center;
    margin: 2px 2px 2px 0;
    padding: 2px 6px;
    font-size: 0.8em;
    font-weight: normal;
    background-color: $light-gray;
    color: $text-color;
    border-radius: 1px;
    white-space: nowrap;
  }

  &__tag-icon {
    margin-left: 4px;
    cursor: pointer;
    color: $color-danger;
    font-size: 0.9em;

    &:hover {
      color: darken($color-danger, 10%);
    }
  }

  &__mas {
    margin: 4px 2px;
    font-size: 0.85em;
    font-weight: 600;
  }

  &__flecha {
    position: absolute;
    right: 0;
    top: 0;
    width: $flecha-ancho;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #999;
    pointer-events: none;
  }

  &__limpiar {
    position: absolute;
    right: $flecha-ancho;
    top: 0;
    width: 25px;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
    color: #999;
    text-decoration: none;

    &:hover {
      color: $color-danger;
    }
  }

  &__spinner {
    position: absolute;
    right: $flecha-ancho + 4px;
    top: 50%;
    transform: translateY(-50%);
    color: #999;
  }

  &__menu {
    display: block;
    position: fixed;
    z-index: 1060;
    width: max-content;
    max-width: calc(100vw - 8px);
    max-height: 300px;
    overflow-y: auto;
    margin: 0;
    padding: 0;
    background-color: $main-background-color;
    border: 1px solid $table-border-color-dark;
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
    scrollbar-color: rgba(166, 172, 179, 1) $main-background-color;

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background-color: $main-background-color;
    }

    &::-webkit-scrollbar-thumb {
      background-color: rgba(166, 172, 179, 1);
      border-radius: 4px;
    }

    // Anular el hover gris de Bootstrap; el resaltado lo controla --resaltada
    > li > a.select-cifra__opcion {
      &:hover,
      &:focus {
        background-color: $main-background-color;
        color: $text-color;
        text-decoration: none;
      }
    }

    // Bootstrap aplica color: inherit en .dropdown-menu > li > a
    > li > a.select-cifra__opcion--grupo {
      background-color: $darkest-gray;
      color: #fff !important;
      font-weight: 600;
      text-align: center;
      cursor: default;

      &:hover,
      &:focus {
        background-color: $darkest-gray !important;
        color: #fff !important;
      }

      &.select-cifra__opcion--resaltada {
        background-color: $color-success !important;
        color: #fff !important;
        cursor: pointer;
      }
    }
  }

  &__opcion {
    display: block;
    padding: 8px 12px;
    clear: both;
    font-weight: normal;
    line-height: 1.42857143;
    color: $text-color;
    white-space: nowrap;
    text-decoration: none;
    background-color: $main-background-color;

    &--resaltada {
      background-color: $color-success !important;
      color: #fff !important;

      .select-cifra__accion {
        color: #fff !important;
      }
    }

    &--seleccionada {
      background-color: darken($gray, 12%);
      color: $text-color;
      font-weight: normal;

      &.select-cifra__opcion--resaltada {
        background-color: $color-danger !important;
        color: #fff !important;
      }
    }

    &--grupo {
      font-weight: 600;
      text-align: center;
      background-color: $darkest-gray;
      color: #fff !important;
      cursor: default;

      &.select-cifra__opcion--resaltada {
        background-color: $color-success !important;
        color: #fff !important;
        cursor: pointer;
      }
    }

    &--deshabilitada {
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    }

    &--vacia {
      color: #999;
      cursor: default;
      pointer-events: none;
    }
  }

  &__accion {
    font-size: 0.8em;
    color: $color-muted;
    margin-left: 8px;
  }
}
</style>
